import type { LocationQueryValue } from 'vue-router'
import { HttpErrorService } from '@/services/HttpError.service'
import { orderErrorHandbook } from '@/models/Errors'
import { HttpService } from '@/services/Http.service'
import type { OrderDto } from '@/models/Order/OrderDto.model'
import type {
  OrderCalculated,
  OrderInfoPageKit,
  OrderRefund,
  OrderRefundBase,
  OrderRefundCalculate,
  OrderRefundInfo,
  OrderStatusResponse,
  Payment,
} from '@/models'

export class OrderService {
  private httpErrorService: HttpErrorService<keyof typeof orderErrorHandbook>
  private endpoint = '/orders'
  private httpService: HttpService

  constructor(httpService: HttpService) {
    this.httpService = httpService
    this.httpErrorService = new HttpErrorService(orderErrorHandbook, 'order')
  }

  async create(order: OrderDto, secure: boolean) {
    const url = [this.endpoint, 'init'].join('/')

    return await this.httpService
      .post<OrderDto>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
          secure,
        },
        fetchConfig: {
          body: order,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async calculate(order: OrderDto, secure: boolean) {
    const url = [this.endpoint, 'calculate'].join('/')

    return await this.httpService
      .post<OrderCalculated>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
          secure,
        },
        fetchConfig: {
          body: order,
          params: {
            isSaleStepCheckDisabled: true,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async fetchOrderInfo(orderId: number, accessCode: LocationQueryValue) {
    const url = [this.endpoint, orderId].join('/')

    return await this.httpService
      .get<OrderInfoPageKit>(url, {
        requestConfig: {
          forceRequest: true,
          needGlobal404: true,
          secure: true,
        },
        fetchConfig: {
          query: {
            accessCode,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async fetchOrderStatus(orderId: number, code: LocationQueryValue) {
    const url = [this.endpoint, 'status'].join('/')

    return await this.httpService
      .get<OrderStatusResponse>(url, {
        requestConfig: {
          forceRequest: true,
        },
        fetchConfig: {
          query: {
            orderId,
            code,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async updateOrder(
    order: OrderDto,
    orderId: number | undefined,
    slug?: string,
  ) {
    const url = [this.endpoint, orderId].join('/')

    return await this.httpService
      .put<Payment>(url, {
        requestConfig: {
          forceRequest: true,
          needGlobal404: false,
          secure: true,
        },
        fetchConfig: {
          query: {
            slug,
          },
          body: order,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  /**
   * запрос получения заказа по email и orderId
   */
  async getOrderInfo(orderId: number, email: string) {
    const url = [this.endpoint, orderId, email].join('/')

    return await this.httpService
      .get<OrderDto>(url, {
        requestConfig: {
          forceRequest: true,
          secure: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  /**
   * запрос получения заказа по email и orderId с проверкой возможности возврата
   * @param body
   */
  async getOrderRefundInfo(body: OrderRefundBase) {
    const url = [this.endpoint, 'refund', 'info'].join('/')

    return await this.httpService
      .post<OrderDto>(url, {
        requestConfig: {
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  /**
   * рассчитывает сумму к возрату
   * @param body
   */
  async calculateOrderRefund(body: OrderRefundCalculate) {
    const url = [this.endpoint, 'refund', 'calculate'].join('/')

    return await this.httpService
      .post<OrderRefundInfo>(url, {
        requestConfig: {
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  /**
   * отправляет подтверждение возврата
   * @param body
   */
  async refundOrder(body: OrderRefund) {
    const url = [this.endpoint, 'refund', 'confirm'].join('/')

    return await this.httpService
      .post(url, {
        requestConfig: {
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async fetchThankYouPage(id: number) {
    const url = [this.endpoint, 'ty', id].join('/')

    return await this.httpService
      .get<OrderInfoPageKit>(url, {
        requestConfig: {
          forceRequest: true,
          needGlobal404: true,
          secure: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }
}
